import React, { useEffect, useMemo, useState } from 'react'
import {
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import { Pagination } from '@material-ui/lab'
import RefreshIcon from '@material-ui/icons/Refresh'
import AddIcon from '@material-ui/icons/Add'
import FolderOpenIcon from '@material-ui/icons/FolderOpenOutlined'
import AssignmentLateIcon from '@material-ui/icons/AssignmentLate'
import EventAvailableIcon from '@material-ui/icons/EventAvailable'
import ScheduleIcon from '@material-ui/icons/Schedule'
import FactCheckIcon from '@material-ui/icons/AssignmentTurnedIn'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import { Container, MessageDrawer } from '../../components'
import { vendorProjectApi } from '../../contexts'
import {
    FilterCriteria,
    PRIORITY_LABELS,
    VENDOR_TASK_STATUS_LABELS,
    VendorProject,
    VendorTask,
    applyProjectFilter,
    explicitSortProjects,
    getProjectBucket,
    MY_PROJECTS_BODY_HEIGHT,
    smartSortProjects,
} from '../../models'
import { useMessageDrawer, usePagination, useUser } from '../../hooks'
import { typographyStyle } from '../../theme/typographyStyle'
import { VendorProjectCard } from './VendorProjectCard'
import { VendorProjectForm } from './VendorProjectForm'
import { CreateProjectModal } from './CreateProjectModal'
import { ProjectViewModal } from './ProjectViewModal'
import { FilterButton } from './FilterButton'

const EMPTY_FILTER: FilterCriteria = {
    dateFrom: null,
    dateTo: null,
    status: null,
    priority: null,
    userId: null,
    sortBy: null,
    sortOrder: null,
}

interface StatTileProps {
    icon: React.ReactNode
    label: string
    value: number
    color: string
    bg: string
}

const StatTile = ({ icon, label, value, color, bg }: StatTileProps) => {
    const theme = useTheme()
    return (
        <Paper
            elevation={0}
            style={{
                flex: 1,
                minWidth: 140,
                padding: theme.spacing(1.5, 2),
                borderRadius: 12,
                border: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing(1.5),
            }}
        >
            <Container
                alignItems="center"
                justifyContent="center"
                style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    backgroundColor: bg,
                    color,
                    flexShrink: 0,
                }}
            >
                {icon}
            </Container>
            <Container direction="column" flex={1}>
                <Container
                    style={{
                        ...typographyStyle(theme.typography.h6),
                        fontWeight: 700,
                        lineHeight: 1.1,
                    }}
                >
                    {value}
                </Container>
                <Container
                    style={{
                        ...typographyStyle(theme.typography.caption),
                        color: theme.palette.text.secondary,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                    }}
                >
                    {label}
                </Container>
            </Container>
        </Paper>
    )
}

export const MyProjectsPage = () => {
    const theme = useTheme()
    const {
        allVendorProjects,
        isLoading,
        error,
        fetchVendorProjects,
    } = vendorProjectApi()
    const { workspaceUser, actions } = useUser()

    const [createOpen, setCreateOpen] = useState(false)
    const [editingProject, setEditingProject] = useState<VendorProject | null>(
        null,
    )
    const [viewingProject, setViewingProject] = useState<VendorProject | null>(
        null,
    )
    const [filter, setFilter] = useState<FilterCriteria>(EMPTY_FILTER)

    const { setChannel } = useMessageDrawer()
    const [messageOpen, setMessageOpen] = useState(false)
    const [messageTitle, setMessageTitle] = useState('Messages')

    useEffect(() => {
        if (!workspaceUser?.active_workspace?.id) return
        actions.getUserList({ params: { my_team: true } }).catch(() => {
            // User-list errors are surfaced by the user store / consumers.
        })
    }, [workspaceUser?.active_workspace?.id])

    const openTaskMessages = (task: VendorTask) => {
        if (!task.channel) return
        setChannel(task.channel)
        setMessageTitle(task.title ? `${task.title} · Messages` : 'Messages')
        setMessageOpen(true)
    }

    const openProjectMessages = (project: VendorProject) => {
        if (!project.channel) return
        setChannel(project.channel)
        setMessageTitle(
            project.title ? `${project.title} · Messages` : 'Messages',
        )
        setMessageOpen(true)
    }

    const visibleProjects = useMemo(() => {
        const filtered = applyProjectFilter(allVendorProjects, filter)
        if (filter.sortBy && filter.sortOrder) {
            return explicitSortProjects(
                filtered,
                filter.sortBy,
                filter.sortOrder,
            )
        }
        return smartSortProjects(filtered)
    }, [allVendorProjects, filter])

    const {
        page,
        setPage,
        pageData,
        numPages,
        start,
        end,
    } = usePagination<VendorProject>(visibleProjects, 20)

    const stats = useMemo(() => {
        let active = 0
        let overdue = 0
        let upcoming = 0
        let completed = 0
        for (const p of allVendorProjects) {
            const b = getProjectBucket(p)
            if (b === 'active') active += 1
            else if (b === 'overdue') overdue += 1
            else if (b === 'upcoming') upcoming += 1
            else if (b === 'completed') completed += 1
        }
        return {
            total: allVendorProjects.length,
            active,
            overdue,
            upcoming,
            completed,
        }
    }, [allVendorProjects])

    const filterActive =
        !!filter.dateFrom ||
        !!filter.dateTo ||
        (filter.priority && filter.priority.length > 0) ||
        (filter.status && filter.status.length > 0) ||
        !!filter.sortBy

    const renderActiveFilterChips = () => {
        if (!filterActive) return null
        const chips: React.ReactNode[] = []
        if (filter.dateFrom || filter.dateTo) {
            chips.push(
                <Chip
                    key="date"
                    size="small"
                    label={`Dates: ${filter.dateFrom ?? '…'} → ${
                        filter.dateTo ?? '…'
                    }`}
                    onDelete={() =>
                        setFilter({
                            ...filter,
                            dateFrom: null,
                            dateTo: null,
                        })
                    }
                />,
            )
        }
        if (filter.priority && filter.priority.length > 0) {
            for (const p of filter.priority) {
                chips.push(
                    <Chip
                        key={`pri-${p}`}
                        size="small"
                        label={`Priority: ${PRIORITY_LABELS[p]}`}
                        onDelete={() =>
                            setFilter({
                                ...filter,
                                priority:
                                    filter.priority &&
                                    filter.priority.filter((x) => x !== p)
                                        .length > 0
                                        ? filter.priority.filter((x) => x !== p)
                                        : null,
                            })
                        }
                    />,
                )
            }
        }
        if (filter.status && filter.status.length > 0) {
            for (const s of filter.status) {
                chips.push(
                    <Chip
                        key={`st-${s}`}
                        size="small"
                        label={`Status: ${VENDOR_TASK_STATUS_LABELS[s]}`}
                        onDelete={() =>
                            setFilter({
                                ...filter,
                                status:
                                    filter.status &&
                                    filter.status.filter((x) => x !== s)
                                        .length > 0
                                        ? filter.status.filter((x) => x !== s)
                                        : null,
                            })
                        }
                    />,
                )
            }
        }
        if (filter.sortBy) {
            chips.push(
                <Chip
                    key="sort"
                    size="small"
                    label={`Sort: ${filter.sortBy} (${
                        filter.sortOrder ?? 'asc'
                    })`}
                    onDelete={() =>
                        setFilter({
                            ...filter,
                            sortBy: null,
                            sortOrder: null,
                        })
                    }
                />,
            )
        }
        return (
            <Container
                alignItems="center"
                style={{
                    gap: theme.spacing(0.75),
                    flexWrap: 'wrap',
                    marginBottom: theme.spacing(1.5),
                }}
            >
                {chips}
                <Button
                    size="small"
                    onClick={() => setFilter(EMPTY_FILTER)}
                    style={{ marginLeft: theme.spacing(0.5) }}
                >
                    Clear all
                </Button>
            </Container>
        )
    }

    return (
        <Container
            direction="column"
            flex={1}
            style={{
                margin: theme.spacing(2),
                overflow: 'hidden',
                height: '100%',
            }}
        >
            {/* Banner Header */}
            <Paper
                elevation={0}
                style={{
                    background: `linear-gradient(135deg, ${theme.palette.brand1.main} 0%, #1f4848 100%)`,
                    color: 'white',
                    padding: theme.spacing(2.5, 3),
                    borderRadius: 12,
                    marginBottom: theme.spacing(2),
                }}
            >
                <Container
                    alignItems="center"
                    style={{ flexWrap: 'wrap', gap: theme.spacing(1) }}
                >
                    <Container direction="column" flex={1}>
                        <Container
                            style={{
                                ...typographyStyle(theme.typography.h5),
                                fontWeight: 700,
                                color: 'white',
                            }}
                        >
                            My Projects
                        </Container>
                        <Container
                            style={{
                                ...typographyStyle(theme.typography.body2),
                                color: 'rgba(255,255,255,0.85)',
                                marginTop: theme.spacing(0.5),
                            }}
                        >
                            Plan, track and manage every project you&apos;ve
                            been assigned in one place.
                        </Container>
                    </Container>
                    <Container
                        alignItems="center"
                        style={{ gap: theme.spacing(1) }}
                    >
                        <Tooltip title="Refresh">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => fetchVendorProjects()}
                                    disabled={isLoading}
                                    style={{
                                        color: 'white',
                                        backgroundColor:
                                            'rgba(255,255,255,0.12)',
                                    }}
                                >
                                    <RefreshIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <FilterButton
                            criteria={filter}
                            onChange={setFilter}
                            onReset={() => setFilter(EMPTY_FILTER)}
                            active={!!filterActive}
                        />
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setCreateOpen(true)}
                            style={{
                                backgroundColor: 'white',
                                color: theme.palette.brand1.main,
                                fontWeight: 700,
                            }}
                        >
                            Create Project
                        </Button>
                    </Container>
                </Container>
            </Paper>

            {/* Stats */}
            {allVendorProjects.length > 0 && (
                <Container
                    style={{
                        gap: theme.spacing(1.5),
                        marginBottom: theme.spacing(2),
                        flexWrap: 'wrap',
                    }}
                >
                    <StatTile
                        icon={<FactCheckIcon />}
                        label="Total"
                        value={stats.total}
                        color={theme.palette.brand1.main}
                        bg="#e8efef"
                    />
                    <StatTile
                        icon={<EventAvailableIcon />}
                        label="Active"
                        value={stats.active}
                        color="#1976d2"
                        bg="#e3f2fd"
                    />
                    <StatTile
                        icon={<AssignmentLateIcon />}
                        label="Overdue"
                        value={stats.overdue}
                        color="#d32f2f"
                        bg="#ffebee"
                    />
                    <StatTile
                        icon={<ScheduleIcon />}
                        label="Upcoming"
                        value={stats.upcoming}
                        color="#7b1fa2"
                        bg="#f3e5f5"
                    />
                    <StatTile
                        icon={<CheckCircleOutlineIcon />}
                        label="Completed"
                        value={stats.completed}
                        color="#2e7d32"
                        bg="#e8f5e9"
                    />
                </Container>
            )}

            {/* Active filter chips */}
            {allVendorProjects.length > 0 && renderActiveFilterChips()}

            {/* Body — fixed viewport-based height so the pagination footer
                below it always stays in view regardless of how many cards fit. */}
            <Container
                direction="column"
                style={{
                    overflowY: 'auto',
                    paddingRight: theme.spacing(1),
                    minHeight: 0,
                    maxHeight: MY_PROJECTS_BODY_HEIGHT,
                    height: MY_PROJECTS_BODY_HEIGHT,
                }}
            >
                {isLoading && allVendorProjects.length === 0 && (
                    <Container
                        justifyContent="center"
                        alignItems="center"
                        style={{ padding: theme.spacing(8) }}
                    >
                        <CircularProgress size={48} />
                    </Container>
                )}

                {!isLoading && error && (
                    <Paper
                        elevation={0}
                        style={{
                            padding: theme.spacing(4),
                            border: `1px solid ${theme.palette.error.light}`,
                            backgroundColor: '#ffebee',
                            borderRadius: 12,
                            textAlign: 'center',
                        }}
                    >
                        <Container
                            direction="column"
                            alignItems="center"
                            style={{ color: theme.palette.error.main }}
                        >
                            <ErrorOutlineIcon
                                style={{ fontSize: 40, marginBottom: 8 }}
                            />
                            <Container
                                style={{
                                    ...typographyStyle(
                                        theme.typography.subtitle1,
                                    ),
                                    fontWeight: 600,
                                }}
                            >
                                {error}
                            </Container>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => fetchVendorProjects()}
                                style={{ marginTop: theme.spacing(2) }}
                            >
                                Try again
                            </Button>
                        </Container>
                    </Paper>
                )}

                {!isLoading && !error && allVendorProjects.length === 0 && (
                    <Paper
                        elevation={0}
                        style={{
                            padding: theme.spacing(6),
                            border: `1px dashed ${theme.palette.divider}`,
                            borderRadius: 12,
                            textAlign: 'center',
                        }}
                    >
                        <Container
                            direction="column"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Container
                                alignItems="center"
                                justifyContent="center"
                                style={{
                                    width: 72,
                                    height: 72,
                                    borderRadius: '50%',
                                    backgroundColor: '#e8efef',
                                    color: theme.palette.brand1.main,
                                    marginBottom: theme.spacing(2),
                                }}
                            >
                                <FolderOpenIcon style={{ fontSize: 36 }} />
                            </Container>
                            <Container
                                style={{
                                    ...typographyStyle(theme.typography.h6),
                                    fontWeight: 700,
                                }}
                            >
                                You don&apos;t have any projects yet
                            </Container>
                            <Container
                                style={{
                                    ...typographyStyle(theme.typography.body2),
                                    color: theme.palette.text.secondary,
                                    marginTop: theme.spacing(1),
                                    marginBottom: theme.spacing(2.5),
                                    textAlign: 'center',
                                }}
                            >
                                Create a project to start tracking work,
                                assigning tasks and collaborating with your
                                team.
                            </Container>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={() => setCreateOpen(true)}
                            >
                                Create your first project
                            </Button>
                        </Container>
                    </Paper>
                )}

                {!isLoading &&
                    !error &&
                    allVendorProjects.length > 0 &&
                    visibleProjects.length === 0 && (
                        <Paper
                            elevation={0}
                            style={{
                                padding: theme.spacing(4),
                                border: `1px dashed ${theme.palette.divider}`,
                                borderRadius: 12,
                                textAlign: 'center',
                            }}
                        >
                            <Container
                                direction="column"
                                alignItems="center"
                                style={{ color: theme.palette.text.secondary }}
                            >
                                No projects match the active filter.
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => setFilter(EMPTY_FILTER)}
                                    style={{ marginTop: theme.spacing(2) }}
                                >
                                    Clear filters
                                </Button>
                            </Container>
                        </Paper>
                    )}

                {pageData.map((p) => (
                    <VendorProjectCard
                        key={p.id}
                        project={p}
                        onEdit={() => setEditingProject(p)}
                        onView={() => setViewingProject(p)}
                        onOpenMessages={openProjectMessages}
                        onOpenTaskMessages={openTaskMessages}
                    />
                ))}
            </Container>

            {/* Pagination footer (pinned, always visible) */}
            {allVendorProjects.length > 0 && (
                <Paper
                    elevation={0}
                    style={{
                        padding: theme.spacing(1.5, 2),
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 12,
                        marginTop: theme.spacing(1),
                        backgroundColor: '#ffffff',
                        flexShrink: 0,
                    }}
                >
                    <Container
                        alignItems="center"
                        style={{
                            flexWrap: 'wrap',
                            gap: theme.spacing(1),
                            justifyContent: 'space-between',
                        }}
                    >
                        <Container
                            style={{
                                ...typographyStyle(theme.typography.body2),
                                color: theme.palette.text.secondary,
                            }}
                        >
                            Showing {start} – {end} of {visibleProjects.length}{' '}
                            project
                            {visibleProjects.length === 1 ? '' : 's'}
                        </Container>
                        <Pagination
                            count={Math.max(numPages, 1)}
                            page={page}
                            color="primary"
                            shape="rounded"
                            onChange={(_, newPage) => setPage(newPage)}
                        />
                    </Container>
                </Paper>
            )}

            <CreateProjectModal
                open={createOpen}
                onClose={() => setCreateOpen(false)}
            />

            <VendorProjectForm
                open={!!editingProject}
                project={editingProject ?? undefined}
                onClose={() => setEditingProject(null)}
            />

            <ProjectViewModal
                open={!!viewingProject}
                project={viewingProject ?? undefined}
                onClose={() => setViewingProject(null)}
            />

            <MessageDrawer
                open={messageOpen}
                title={messageTitle}
                onClose={() => setMessageOpen(false)}
            />
        </Container>
    )
}
